// Shared styles for ListingPage section components, formerly ListingPage.module.css.
//
// Two kinds of exports:
//  1. `errorTextSx` — a normal sx for the former `.errorText`, used on owned elements
//     (ListingPage.error.js <p>, SectionReviews.js <h2>).
//  2. Page-prefixed plain class names + a `ListingPageGlobalStyles` <GlobalStyles> component
//     for the five classes that are forwarded as a className-type prop INTO a child component
//     (NamedLink, EditIcon, ResponsiveImage rootClassName, BasicUI Button, Modal scrollLayer).
//     These children apply the class to their own internal element, so we keep passing the class
//     but back it with an emotion <GlobalStyles> rule instead of a css-module hash. Emotion
//     <GlobalStyles> are inserted as base styles (before component sx), so a child's own sx still
//     wins on conflicts — matching the former css-module precedence. The component dedupes itself
//     across instances, so rendering <ListingPageGlobalStyles /> once per consumer is safe.
//     Page-prefixed names avoid collision with same-named source classes elsewhere.
import GlobalStyles from '@mui/material/GlobalStyles';
import React from 'react';

// former ListingPage.module.css .errorText
export const errorTextSx = (theme) => ({
  color: theme.tokens.failColor,
  margin: '24px',
});

export const LISTING_PAGE_EDIT_LISTING_LINK = 'listingPageEditListingLink';
export const LISTING_PAGE_EDIT_ICON = 'listingPageEditIcon';
export const LISTING_PAGE_ROOT_FOR_SHARE_IMAGE = 'listingPageRootForShareImage';
export const LISTING_PAGE_SHARE_LINK_COPY_BUTTON = 'listingPageShareLinkCopyButton';
export const LISTING_PAGE_CAROUSEL_MODAL_SCROLL_LAYER = 'listingPageCarouselModalScrollLayer';
export const LISTING_PAGE_GALLERY_MODAL_SCROLL_LAYER = 'listingPageGalleryModalScrollLayer';

export const ListingPageGlobalStyles = () => (
  <GlobalStyles
    styles={(theme) => ({
      // former ListingPage.module.css .editListingLink
      // (@apply --marketplaceDefaultFontStyles + own md override; both have a min-width:900px block → merged)
      [`.${LISTING_PAGE_EDIT_LISTING_LINK}`]: {
        ...theme.mixins.marketplaceDefaultFontStyles,
        flexShrink: 0,
        margin: 0,
        padding: '14px 24px 11px 12px',
        color: theme.tokens.matterColorNegative,
        transition: theme.tokens.transitionStyleButton,
        '&:hover': {
          color: theme.tokens.matterColorLight,
          textDecoration: 'none',
        },
        '@media (min-width:900px)': {
          ...theme.mixins.marketplaceDefaultFontStyles['@media (min-width:900px)'],
          margin: 0,
          padding: '25px 24px 22px 12px',
        },
      },
      // former ListingPage.module.css .editIcon
      [`.${LISTING_PAGE_EDIT_ICON}`]: {
        margin: '-6px 7px 0 0',
      },
      // former ListingPage.module.css .rootForShareImage
      [`.${LISTING_PAGE_ROOT_FOR_SHARE_IMAGE}`]: {
        position: 'relative',
        width: 'auto',
        height: 'auto',
        borderRadius: theme.tokens.borderRadius,
      },
      // former ListingPage.module.css .shareLinkCopyButton (@apply --marketplaceSmallFontStyles; no md block)
      [`.${LISTING_PAGE_SHARE_LINK_COPY_BUTTON}`]: {
        ...theme.mixins.marketplaceSmallFontStyles,
        flex: 1,
        padding: '10px',
        minHeight: 'auto',
        '& svg': {
          height: '14px',
          width: 'auto',
        },
      },
      // former ListingPage.module.css .carouselModalScrollLayer
      // KEPT as a plain scrollLayerClassName (NOT scrollLayerSx) because Modal's scrollLayerSx is
      // additive over the scroll-layer base; the class form replaces it, matching the original.
      [`.${LISTING_PAGE_CAROUSEL_MODAL_SCROLL_LAYER}`]: {
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(1, 1, 1, 0.92)',
      },
      // Photo gallery overlay: white, scrollable full-screen layer.
      // A scrollLayerClassName REPLACES Modal's scroll-layer base styles, so
      // overflow/scrolling must be declared here.
      [`.${LISTING_PAGE_GALLERY_MODAL_SCROLL_LAYER}`]: {
        width: '100vw',
        height: '100vh',
        backgroundColor: theme.tokens.matterColorLight,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
      },
    })}
  />
);
